import React, { useRef } from "react";

import { Box } from "@mui/material";
import { Extension } from "@tiptap/core";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { TextSelection } from "@tiptap/pm/state";
import { type Editor, EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { LinkBubbleMenu, LinkBubbleMenuHandler, RichTextEditorProvider, useRichTextEditorContext } from "mui-tiptap";
import type { TextCardConfig } from "store/types";

import { FloatingToolbar } from "./FloatingToolbar";

const editorContentSx = {
  width: "100%",
  "& .tiptap": {
    color: "#374151",
    padding: 0,
    outline: "none",
    minHeight: 60,
    fontSize: 13,
    "& p": {
      lineHeight: 1.7,
      margin: 0,
      marginBottom: "8px"
    },
    "& p:last-child": {
      marginBottom: 0
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      lineHeight: 1.4,
      margin: 0,
      marginBottom: "8px"
    },
    "& h1:last-child, & h2:last-child, & h3:last-child, & h4:last-child, & h5:last-child, & h6:last-child": {
      marginBottom: 0
    },
    "& ul, & ol": {
      paddingLeft: "1.5em"
    },
    "& li": {
      marginLeft: "18px"
    },
    "& a:not([data-type='mention'])": {
      color: "#26A6AE !important",
      "&:hover": {
        textDecoration: "underline"
      }
    },
    "& p.is-editor-empty:first-of-type::before": {
      content: "attr(data-placeholder)",
      float: "left",
      height: 0,
      pointerEvents: "none",
      color: "#9eacb5"
    }
  }
};

// Registers a `dir` global attribute on block nodes so the built-in
// setTextDirection command (from @tiptap/core) can persist & render LTR/RTL.
// The official TextDirection extension only registers `dir` when a default
// direction is configured, which would force `dir` onto every exported node;
// here we keep the default null so unset nodes export no `dir`.
const TextDirection = Extension.create({
  name: "textDirection",
  addGlobalAttributes() {
    return [
      {
        types: ["heading", "paragraph"],
        attributes: {
          dir: {
            default: null,
            parseHTML: (element) => element.getAttribute("dir") || null,
            renderHTML: (attributes) => (attributes.dir ? { dir: attributes.dir } : {})
          }
        }
      }
    ];
  }
});

const extensions = [
  StarterKit,
  Placeholder.configure({
    placeholder: "Type, or press Space to ask AI…",
    showOnlyCurrent: true
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"]
  }),
  TextDirection,
  LinkBubbleMenuHandler
];

/**
 * Wrapper that subscribes to editor transactions so the bubble menu
 * re-renders immediately when `openLinkBubbleMenu` mutates storage.
 * Without this, TipTap v3's `useEditor` no longer triggers re-renders
 * on every transaction, leaving `LinkBubbleMenu` stale until an
 * unrelated React state change forces a re-render.
 */
const LinkBubbleMenuSync: React.FC = () => {
  const editor = useRichTextEditorContext();
  useEditorState({ editor, selector: (snapshot) => snapshot.transactionNumber });
  return <LinkBubbleMenu />;
};

interface Props {
  config: TextCardConfig;
  isPreview?: boolean;
  onChange?: (content: string) => void;
  /** Called when the user presses Space in an empty editor — transforms the section into an AI card. */
  onTransformToAI?: () => void;
}

const TextCardEditor: React.FC<Props> = ({ config, onChange, onTransformToAI }) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onTransformRef = useRef(onTransformToAI);
  onTransformRef.current = onTransformToAI;

  const editorRef = useRef<Editor | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const revertingRef = useRef(false);

  // The card content area (CardWrapper) has a fixed height with overflow hidden.
  // That clipping box is the direct DOM parent of this editor's wrapper.
  const isContentOverflowing = () => {
    const clip = wrapperRef.current?.parentElement;
    return !!clip && clip.scrollHeight > clip.clientHeight;
  };

  const editor = useEditor({
    extensions,
    content: config.content,
    editorProps: {
      handleKeyDown: (_view, event) => {
        // Space on an empty section → switch to AI mode (ask a question via the settings panel)
        if (event.key === " " && onTransformRef.current && editorRef.current?.isEmpty) {
          event.preventDefault();
          onTransformRef.current();
          return true;
        }
        return false;
      }
    },
    onUpdate: ({ editor, transaction }) => {
      // Skip the synthetic update produced by our own revert dispatch below.
      if (revertingRef.current) return;

      // If a content change pushed the text past the card's fixed height, undo
      // exactly that change so the user can't type/paste beyond the block.
      if (transaction.docChanged && isContentOverflowing()) {
        revertingRef.current = true;
        try {
          const view = editor.view;
          const before = transaction.before;
          const diff = view.state.doc.content.size - before.content.size;
          const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, before.content);
          const pos = Math.max(1, Math.min(transaction.selection.from - diff, tr.doc.content.size));
          tr.setSelection(TextSelection.near(tr.doc.resolve(pos)));
          tr.setMeta("addToHistory", false);
          view.dispatch(tr);
        } finally {
          revertingRef.current = false;
        }
        return;
      }

      onChangeRef.current?.(editor.getHTML());
    }
  });

  editorRef.current = editor;

  if (!editor) return null;

  return (
    <Box
      ref={wrapperRef}
      sx={{ p: "12px 16px", cursor: "text" }}
      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
    >
      <RichTextEditorProvider editor={editor}>
        <Box sx={editorContentSx}>
          <EditorContent editor={editor} />
        </Box>
        <FloatingToolbar />
        <LinkBubbleMenuSync />
      </RichTextEditorProvider>
    </Box>
  );
};

const TextCardPreview: React.FC<{ content: string }> = ({ content }) => {
  const editor = useEditor({
    extensions,
    content,
    editable: false
  });

  if (!editor) return null;

  return (
    <Box sx={{ p: "20px 24px", ...editorContentSx }}>
      <EditorContent editor={editor} />
    </Box>
  );
};

export const TextCard: React.FC<Props> = (props) => {
  if (props.isPreview) {
    return <TextCardPreview content={props.config.content} />;
  }
  return <TextCardEditor {...props} />;
};
